<template>
  <div class="header">
    <div class="left">
      <button
        type="button"
        class="mobile-navigation-toggle"
        aria-label="打开主导航"
        aria-controls="primary-navigation"
        :aria-expanded="String(mobileNavigationOpen)"
        @click="$emit('open-navigation')"
      >
        <i class="el-icon-menu" aria-hidden="true"></i>
      </button>
      <span class="icon"></span>
      <span class="page-title">{{ pageTitle }}</span>
    </div>
    <div class="right">
      <button
        v-if="isAuthenticated"
        class="space-entry radius"
        type="button"
        :title="`当前空间：${currentSpaceName}`"
        aria-label="打开空间与成员管理"
        @click="showSpaceManager = true"
      >
        <i class="el-icon-office-building" aria-hidden="true"></i>
        <span class="space-entry__label">{{ currentSpaceName }}</span>
      </button>
      <button
        class="admin-entry radius"
        v-if="canAccessAdminConsole"
        aria-label="打开管理后台"
        title="管理后台"
        type="button"
        @click="openAdminConsole"
      >
        <i class="el-icon-setting mobile-action-icon" aria-hidden="true"></i>
        <span class="action-label">管理后台</span>
      </button>
      <div class="user-role radius">{{ roleLabel }}</div>
      <div class="fans-count radius">{{ creditsLabel }}</div>
      <button class="signin-btn radius" v-if="isAuthenticated" aria-label="打开签到" title="签到" type="button" @click="showSignin = true">
        <i class="el-icon-date mobile-action-icon" aria-hidden="true"></i>
        <span class="action-label">签到</span>
      </button>
      <button class="message radius" aria-label="打开消息中心" title="消息" type="button" @click="handleMessageClick">
        <i class="el-icon-message mobile-action-icon" aria-hidden="true"></i>
        <span class="action-label">消息</span>
        <span v-if="unreadCount > 0" class="unread-badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
      </button>
      <div class="border"></div>
      <div class="login-btn" v-if="!isAuthenticated" @click="showLogin = true">登录</div>
      <el-popover
        v-else
        v-model="userMenuVisible"
        placement="bottom-end"
        width="250"
        popper-class="user-popover"
        trigger="click"
      >
        <button
          slot="reference"
          type="button"
          class="user"
          aria-label="打开账号菜单"
          aria-haspopup="menu"
          :aria-expanded="String(userMenuVisible)"
        >
          <img class="user-avatar" :src="currentAvatarURL" alt="" />
          <span>{{ username }}</span>
        </button>
        <div>
          <div class="user-left">
            <div class="avatar bg-box">
              <img :src="currentAvatarURL" alt="" />
            </div>
            <div class="info">
              <div class="name">{{ username }}</div>
            </div>
          </div>
          <div class="info-item">
            <div class="vip">
              <div>用户身份: <span class="vipclass">{{ roleLabel }}</span></div>
              <div>积分余额: <span class="points">{{ creditsValueLabel }}</span></div>
              <div class="top-up" @click="requestMember">充值</div>
            </div>
            <button
              type="button"
              class="item"
              v-for="item in infoList"
              :key="item.name"
              @click="handleMenuAction(item)"
            >
              <svg-icon :name="item.icon" class="icon"></svg-icon>
              <div>{{ item.name }}</div>
            </button>
            <button type="button" class="item items" @click="handleLogout">
              <svg-icon name="logout" class="icon"></svg-icon>
              <div>退出登录</div>
            </button>
          </div>
        </div>
      </el-popover>
    </div>

    <LoginDialog :visible.sync="showLogin" />
    <CheckDialog :visible.sync="showSignin" />
    <SpaceManagementDialog :visible.sync="showSpaceManager" />
  </div>
</template>

<script>
import LoginDialog from "./LoginDialog.vue";
import CheckDialog from "./CheckDialog.vue";
import SpaceManagementDialog from "./SpaceManagementDialog.vue";
import { authState, logout } from "@/services/auth";
import { spaceDisplayLabel, userDisplayLabel } from "@/services/identityLabels";
import {
  memberBillingState,
  refreshMemberBillingOverview,
  resolveMemberIdentityLabel,
} from "@/services/memberStatus";
import {
  clearMyCreditsOverview,
  extractCreditBalance,
  myCreditsState,
  refreshMyCreditsOverview,
} from "@/services/myCredits";
import defaultAvatar from "@/assets/image/Sunny.png";
import { getUnreadMessageCount } from "@/services/messages";
import { adminDefaultPath, canAccessAdminConsole } from "@/services/adminAccess";

export default {
  name: "header-view",
  components: {
    LoginDialog,
    CheckDialog,
    SpaceManagementDialog,
  },
  props: {
    mobileNavigationOpen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showLogin: false,
      showSignin: false,
      showSpaceManager: false,
      userMenuVisible: false,
      unreadCount: 0,
      unreadTimer: null,
      infoList: [
        { name: "空间与成员", icon: "team", action: "space" },
        { name: "开发者中心", icon: "document", action: "developer" },
        { name: "我的项目", icon: "project", action: "project" },
        { name: "密码修改", icon: "password", action: "password" },
        { name: "用户中心", icon: "usercenter", action: "usercenter" },
      ],
    };
  },
  computed: {
    isProfileLayout() {
      return this.$route.path === "/personal-center";
    },
    isAuthenticated() {
      return !!(authState.session && authState.session.id);
    },
    canAccessAdminConsole() {
      const user = authState.session && authState.session.user;
      return canAccessAdminConsole(user);
    },
    currentSpaceName() {
      const space = authState.session && authState.session.current_space
        ? authState.session.current_space
        : null;
      return spaceDisplayLabel(space, authState.session && authState.session.user);
    },
    username() {
      return authState.session && authState.session.user
        ? userDisplayLabel(authState.session.user, "已登录用户")
        : "未登录";
    },
    currentAvatarURL() {
      if (!(authState.session && authState.session.user)) {
        return defaultAvatar;
      }
      return authState.session.user.avatar_url || defaultAvatar;
    },
    creditsValue() {
      if (!this.isAuthenticated) {
        return null;
      }
      if (myCreditsState.loading && !myCreditsState.loaded) {
        return null;
      }
      return extractCreditBalance(myCreditsState.overview || null);
    },
    creditsValueLabel() {
      if (this.creditsValue == null) {
        return "--";
      }
      return this.creditsValue.toLocaleString("zh-CN");
    },
    creditsLabel() {
      if (!this.isAuthenticated) {
        return "积分余额 --";
      }
      if (myCreditsState.loading && !myCreditsState.loaded) {
        return "积分余额 ...";
      }
      const balance = extractCreditBalance(myCreditsState.overview || null);
      return `积分余额 ${balance.toLocaleString("zh-CN")}`;
    },
    roleLabel() {
      const user = authState.session && authState.session.user
        ? authState.session.user
        : null;
      if (!user) {
        return "访客";
      }
      return resolveMemberIdentityLabel(user, memberBillingState.overview);
    },
    pageTitle() {
      return (this.$route.meta && this.$route.meta.title) || "页面";
    },
  },
  watch: {
    isAuthenticated: {
      immediate: true,
      handler(value) {
        if (!value) {
          clearMyCreditsOverview();
          this.stopUnreadPolling();
          return;
        }
        this.loadCreditsOverview();
        this.loadMemberIdentity();
        this.pollUnreadCount();
      },
    },
  },
  mounted() {
    window.addEventListener("orion-message-unread-changed", this.fetchUnreadCount);
  },
  beforeDestroy() {
    window.removeEventListener("orion-message-unread-changed", this.fetchUnreadCount);
    this.stopUnreadPolling();
  },
  methods: {
    async pollUnreadCount() {
      await this.fetchUnreadCount();
      this.stopUnreadPolling();
      this.unreadTimer = setInterval(() => { this.fetchUnreadCount(); }, 30000);
    },
    stopUnreadPolling() {
      if (this.unreadTimer) { clearInterval(this.unreadTimer); this.unreadTimer = null; }
    },
    async fetchUnreadCount() {
      try {
        const payload = await getUnreadMessageCount();
        this.unreadCount = Number(payload && payload.count) || 0;
      } catch (e) { /* silent */ }
    },
    async loadCreditsOverview() {
      try {
        await refreshMyCreditsOverview();
      } catch (error) {
        // Keep header quiet; detailed errors belong to explicit credits pages.
      }
    },
    async loadMemberIdentity() {
      if (!this.isAuthenticated || memberBillingState.loading) {
        return;
      }
      if (memberBillingState.loaded) {
        return;
      }
      try {
        await refreshMemberBillingOverview();
      } catch (error) {
        // Keep header quiet; profile and membership pages surface detailed errors.
      }
    },
    openAdminConsole() {
      const user = authState.session && authState.session.user;
      const target = this.$router.resolve({ path: adminDefaultPath(user) });
      window.open(target.href, "_blank", "noopener,noreferrer");
    },
    requestMember() {
      this.userMenuVisible = false;
      this.$emit("request-member");
    },
    handleMessageClick() {
      this.$router.push("/messages");
    },
    handleMenuAction(item) {
      if (!item) {
        return;
      }
      this.userMenuVisible = false;
      switch (item.action) {
        case "developer": {
          const target = this.$router.resolve({ path: "/developer" });
          window.open(target.href, "_blank", "noopener,noreferrer");
          return;
        }
        case "usercenter":
          this.$router.push("/user-detail");
          return;
        case "password":
          this.$message.info("密码修改能力后续接入");
          return;
        case "project":
          this.$message.info("项目中心后续接入");
          return;
        case "space":
          this.showSpaceManager = true;
          return;
        default:
          return;
      }
    },
    async handleLogout() {
      this.userMenuVisible = false;
      try {
        await logout();
        this.$message.success("已退出登录");
      } catch (error) {
        this.$message.error(error.message || "退出失败");
      }
    },
  },
};
</script>

<style lang="scss" scoped>
:global(.user-popover) {
  max-height: calc(100vh - 24px);
  padding: 0 !important;
  overflow-y: auto;
}

.info-item {
  .vip {
    height: 100px;
    background-image: url("@/assets/image/vip-bg.jpg");
    background-size: cover;
    border-radius: 6px;
    margin: 0 20px 10px;
    padding: 10px 16px 16px;

    div {
      font-size: 14px;
      color: #333333;
      line-height: 26px;
    }

    .vipclass {
      color: #333333;
    }

    .points {
      color: #e96800;
    }

    .top-up {
      width: 56px;
      height: 22px;
      background: linear-gradient(90deg, #393838 0%, #5c5c5c 50%, #777777 100%);
      border-radius: 11px;
      font-size: 12px;
      color: #ffffff;
      line-height: 22px;
      text-align: center;
      margin-top: 4px;
      cursor: pointer;
    }
  }

  .item {
    display: flex;
    width: calc(100% - 40px);
    align-items: center;
    gap: 16px;
    justify-content: flex-start;
    border: 0;
    border-radius: 6px;
    height: 32px;
    margin: 0 20px;
    padding: 0 18px;
    background: transparent;
    font: inherit;
    font-size: 14px;
    color: #333333;
    cursor: pointer;

    &:hover {
      background-color: #ebf0f4;
    }

    .icon {
      width: 16px;
      height: 16px;
    }
  }

  .items {
    width: 100%;
    justify-content: center;
    color: #aaaaaa;
    margin: 0;
    padding: 0;
    border-top: solid 1px #eeeeee;
    height: 40px;
  }
}

.user-left {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 20px 27px;

  .avatar {
    width: 70px;
    height: 70px;
    border-radius: 50%;
    overflow: hidden;
    box-shadow: 0px 6px 20px 0px rgba(104, 131, 176, 0.4);

    img {
      display: block;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      object-fit: cover;
    }
  }

  .info {
    .name {
      font-size: 16px;
      color: #333;
    }
  }
}

.header {
  height: 63px;
  background: #ffffff;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;

  .left {
    display: flex;
    align-items: center;

    .icon {
      width: 6px;
      height: 20px;
      background-color: #ff881e;
      border-radius: 3px;
      margin-right: 8px;
    }

    .page-title {
      font-size: 18px;
      line-height: 80px;
      color: #464646;
    }
  }
}

.mobile-navigation-toggle {
  display: none;
}

.mobile-action-icon {
  display: none;
}

.right {
  display: flex;
  align-items: center;

  .radius {
    padding: 8px 20px;
    border: 0;
    font: inherit;
    border-radius: 15px;
    font-size: 13px;
    margin-right: 13px;
    cursor: pointer;
  }

  .user-role {
    background-color: #313845;
    color: #ffffff;
  }

  .space-entry {
    display: inline-flex;
    max-width: 190px;
    align-items: center;
    gap: 7px;
    border: 1px solid #e2e6eb;
    background: #f7f8fa;
    color: #2a303a;

    &:hover {
      border-color: #f78001;
      color: #f78001;
    }

    .space-entry__label {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  .admin-entry {
    background-color: #fff7eb;
    border: 1px solid #f78001;
    color: #f78001;
  }

  .border {
    height: 22px;
    border: solid 1px #dddddd;
  }

  .fans-count {
    color: #ffffff;
    background-image: linear-gradient(94deg, #fe752b 0%, #ff9805 100%);
  }

  .signin-btn {
    padding: 7px 18px;
    background-image: linear-gradient(94deg, #fe752b 0%, #ff9805 100%);
    color: #fff;
    cursor: pointer;
  }

  .message {
    padding: 7px 18px;
    background-color: #ffffff;
    border: solid 1px #f78001;
    color: #f78001;
    position: relative;
    cursor: pointer;
  }

  .unread-badge {
    position: absolute;
    top: -6px;
    right: -6px;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    background: #f56c6c;
    color: #fff;
    font-size: 11px;
    line-height: 18px;
    text-align: center;
  }

  .login-btn {
    padding: 7px 24px;
    background-image: linear-gradient(94deg, #fe752b 0%, #ff9805 100%);
    color: #fff;
    font-size: 13px;
    margin-left: 18px;
    border-radius: 4px;
    cursor: pointer;
  }

  .login-btn:hover {
    opacity: 0.9;
  }

  .user {
    padding: 0;
    border: 0;
    background: transparent;
    font: inherit;
    font-size: 14px;
    color: #464646;
    display: flex;
    align-items: center;
    cursor: pointer;

    .user-avatar {
      display: block;
      width: 32px;
      height: 32px;
      background-color: #ffffff;
      box-shadow: 0px 2px 8px 0px rgba(131, 156, 185, 0.3);
      border-radius: 50%;
      object-fit: cover;
      margin: 0 8px 0 18px;
    }
  }
}

@media (max-width: 767px) {
  :global(.user-popover) {
    max-width: calc(100vw - 24px);
  }

  .header {
    min-width: 0;
    padding: 0 12px;
    gap: 10px;

    .left {
      flex: 1 1 auto;
      min-width: 0;

      .icon {
        display: none;
      }

      .page-title {
        min-width: 0;
        overflow: hidden;
        line-height: 1.25;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }

  .mobile-navigation-toggle {
    display: inline-flex;
    flex: 0 0 36px;
    width: 36px;
    height: 36px;
    margin-right: 8px;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: #333333;
    cursor: pointer;
    font-size: 20px;
  }

  .right {
    flex: 0 0 auto;

    .user-role,
    .fans-count,
    .border {
      display: none;
    }

    .admin-entry,
    .space-entry,
    .signin-btn,
    .message {
      display: inline-flex;
      width: 32px;
      height: 32px;
      margin-right: 6px;
      padding: 0;
      align-items: center;
      justify-content: center;
      border-radius: 6px;
      font-size: 17px;

      .action-label {
        display: none;
      }

      .mobile-action-icon {
        display: inline-block;
      }
    }

    .space-entry {
      .space-entry__label {
        display: none;
      }
    }

    .login-btn {
      margin-left: 0;
      padding: 7px 16px;
    }

    .user {
      > span {
        display: none;
      }

      .user-avatar {
        margin: 0;
      }
    }
  }
}
</style>
